import React, { useEffect, useState } from 'react';
import { message, Modal, Form, Input, InputNumber, Select } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { formationAPI } from '../../services/api';

// ═══════════════════════════════════════════════
// /formation/tops — 层位标记 CRUD
// ═══════════════════════════════════════════════
const TH = {
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', accentLight: 'rgba(194,83,58,0.08)',
  headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  warn: '#D4770C',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};

const MARKER_LABEL: Record<string, string> = {
  horizon: '地层界面', formation_top: '组顶', kop: '造斜点',
  landing: '入靶点', toe: '靶端',
};

type Top = { id: number; name: string; depth: number; marker_type: string };

export default function FormationTopsPage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;

  const [tops, setTops] = useState<Top[]>([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<Top | null | 'new'>(null);
  const [form] = Form.useForm();

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await formationAPI.listTops(projectId);
      const ts: Top[] = Array.isArray(data) ? data : (data?.results ?? []);
      setTops(ts.sort((a, b) => a.depth - b.depth));
    } catch (e: any) {
      message.error(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectId]);

  const submit = async () => {
    const vals = await form.validateFields();
    if (!projectId) return;
    try {
      if (edit === 'new') {
        await formationAPI.createTop({ project: projectId, ...vals });
        message.success('已新增');
      } else if (edit) {
        await formationAPI.updateTop(edit.id, vals);
        message.success('已更新');
      }
      setEdit(null);
      form.resetFields();
      await load();
    } catch (e: any) {
      const d = e?.response?.data;
      message.error(typeof d === 'string' ? d : d?.detail || '保存失败');
    }
  };

  const remove = (t: Top) => {
    Modal.confirm({
      title: `删除 "${t.name}"?`,
      onOk: async () => {
        try {
          await formationAPI.deleteTop(t.id);
          message.success('已删除');
          await load();
        } catch { message.error('删除失败'); }
      },
    });
  };

  const openEdit = (t: Top | 'new') => {
    setEdit(t);
    form.resetFields();
    if (t !== 'new') {
      form.setFieldsValue(t);
    } else {
      form.setFieldsValue({ marker_type: 'horizon' });
    }
  };

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#fff', fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${TH.headerBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>层位标记</h2>
          <span style={{ fontSize: 12, color: TH.textMuted }}>{tops.length} 个</span>
        </div>
        <button onClick={() => openEdit('new')} style={{
          padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
          background: TH.accent, color: '#fff', border: 'none', cursor: 'pointer',
        }}>+ 新增标记</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '8px 20px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: TH.panelHeaderBg, position: 'sticky', top: 0 }}>
              {['名称', '深度(m)', '类型', '操作'].map((h) => (
                <th key={h} style={{ padding: '6px 10px', textAlign: 'left', borderBottom: `1px solid ${TH.headerBorder}`, color: TH.textSec, fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} style={{ padding: 30, textAlign: 'center', color: TH.textMuted }}>加载中…</td></tr>
            ) : tops.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 30, textAlign: 'center', color: TH.textMuted }}>无层位标记 — 用来标 KOP / 入靶点 / 关键界面</td></tr>
            ) : tops.map((t) => (
              <tr key={t.id} style={{ borderBottom: '1px solid #F0EDE4' }}>
                <td style={{ padding: '5px 10px', fontWeight: 600, color: TH.text }}>{t.name}</td>
                <td style={{ padding: '5px 10px', fontFamily: TH.fontMono }}>{t.depth.toFixed(2)}</td>
                <td style={{ padding: '5px 10px' }}>
                  <span style={{ padding: '2px 8px', fontSize: 11, borderRadius: 10,
                    background: TH.accentLight, color: TH.accent }}>
                    {MARKER_LABEL[t.marker_type] || t.marker_type}
                  </span>
                </td>
                <td style={{ padding: '5px 10px' }}>
                  <button onClick={() => openEdit(t)} style={btnSm}>编辑</button>
                  <button onClick={() => remove(t)} style={{ ...btnSm, marginLeft: 4, color: TH.warn }}>删</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={edit !== null}
        title={edit === 'new' ? '新增层位标记' : `编辑 ${(edit as Top)?.name}`}
        onCancel={() => { setEdit(null); form.resetFields(); }}
        onOk={submit}
        okText="保存" cancelText="取消"
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input placeholder="例: 龙马溪顶 / KOP / 入靶点 A" />
          </Form.Item>
          <Form.Item name="depth" label="深度 (m)" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} step={0.1} />
          </Form.Item>
          <Form.Item name="marker_type" label="类型">
            <Select options={Object.entries(MARKER_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

const btnSm: React.CSSProperties = {
  padding: '2px 8px', fontSize: 11, border: `1px solid ${TH.headerBorder}`,
  background: '#fff', borderRadius: 3, cursor: 'pointer', color: TH.textSec,
};
